<script setup lang="ts">
import { computed, reactive, ref, watch } from "vue";

import AppButton from "@shared/ui/components/AppButton.vue";
import AppModal from "@shared/ui/components/AppModal.vue";

import { useFileUpload } from "@features/file-transfer/useFileUpload";

import type { FileAttachment } from "shared";

const props = defineProps<{ chatId: string; disabled: boolean }>();
const emit = defineEmits<{
    send: [caption: string, attachments: FileAttachment[]];
}>();

const { uploadFile, deleteFile } = useFileUpload();

interface PendingFile {
    file: File;
    status: "uploading" | "done" | "error";
    progress: number;
    attachment?: FileAttachment;
    // Что реально уходит на сервер (оригинал или пережатая картинка).
    sendSize: number;
    // Превью для картинок (objectURL исходника — до всякой загрузки).
    thumb?: string;
    // Отмена активной загрузки (сервер подчищается в транспорте).
    controller?: AbortController;
    // Текст ошибки загрузки (показывается в строке файла).
    error?: string;
}

const pending = ref<PendingFile[]>([]);
const caption = ref("");

// «Оптимизировать файлы»: картинки пережимаются перед шифрованием/заливкой.
// Выбор запоминается между сессиями.
const optimize = ref(localStorage.getItem("mc_optimize_files") !== "0");
watch(optimize, (v) => {
    localStorage.setItem("mc_optimize_files", v ? "1" : "0");
});

const OPT_MAX_SIDE = 2560;

// gif не трогаем (canvas убил бы анимацию).
function canOptimize(file: File): boolean {
    return file.type.startsWith("image/") && file.type !== "image/gif";
}

// Даунскейл до OPT_MAX_SIDE + пережатие в JPEG. Если не стало меньше (или
// формат не осилили) — отправляем оригинал.
async function optimizeImage(file: File): Promise<File> {
    try {
        const bitmap = await createImageBitmap(file);
        const scale = Math.min(
            1,
            OPT_MAX_SIDE / Math.max(bitmap.width, bitmap.height)
        );
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(bitmap.width * scale);
        canvas.height = Math.round(bitmap.height * scale);
        const ctx = canvas.getContext("2d");
        if (!ctx) {
            bitmap.close();
            return file;
        }
        ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
        bitmap.close();
        const blob = await new Promise<Blob | null>((resolve) =>
            canvas.toBlob(resolve, "image/jpeg", 0.85)
        );
        if (!blob || blob.size >= file.size) {
            return file;
        }
        return new File([blob], file.name.replace(/\.\w+$/, "") + ".jpg", {
            type: "image/jpeg",
        });
    } catch {
        return file;
    }
}

const modalVisible = computed(() => pending.value.length > 0);
const uploading = computed(() =>
    pending.value.some((p) => p.status === "uploading")
);
const readyCount = computed(
    () => pending.value.filter((p) => p.status === "done").length
);
// Есть ли что оптимизировать — иначе чекбокс не показываем.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const hasOptimizable = computed(() =>
    pending.value.some((p) => canOptimize(p.file))
);

function formatSize(bytes: number): string {
    if (bytes < 1024) {
        return `${bytes} Б`;
    }
    if (bytes < 1024 * 1024) {
        return `${(bytes / 1024).toFixed(0)} КБ`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
}

// Метка типа: расширение имени, иначе подтип mime.
function fileExt(p: PendingFile): string {
    const dot = p.file.name.lastIndexOf(".");
    if (dot > 0 && dot < p.file.name.length - 1) {
        return p.file.name
            .slice(dot + 1)
            .toUpperCase()
            .slice(0, 4);
    }
    const sub = p.file.type.split("/")[1];
    return (sub ?? "FILE").toUpperCase().slice(0, 4);
}

async function onFileChange(e: Event) {
    const input = e.target as HTMLInputElement;
    if (!input.files) {
        return;
    }
    const files = Array.from(input.files);
    input.value = "";
    await addFiles(files);
}

async function uploadEntry(entry: PendingFile): Promise<void> {
    entry.status = "uploading";
    entry.progress = 0;
    entry.attachment = undefined;
    entry.controller = new AbortController();
    try {
        const source =
            optimize.value && canOptimize(entry.file)
                ? await optimizeImage(entry.file)
                : entry.file;
        entry.sendSize = source.size;
        entry.attachment = await uploadFile(
            source,
            props.chatId,
            (fraction) => {
                entry.progress = fraction;
            },
            entry.controller.signal
        );
        entry.status = "done";
    } catch (e) {
        // Отменённая запись (удаление/перезаливка) — статус не трогаем.
        if (!(e instanceof DOMException && e.name === "AbortError")) {
            entry.status = "error";
            // Причина — в строку файла: в релизной сборке консоли нет.
            entry.error = e instanceof Error ? e.message : String(e);
        }
    }
}

async function addFiles(files: File[]) {
    if (!files.length) {
        return;
    }
    // reactive() до push: замыкания ниже мутируют те же прокси, что рендерит
    // список (сырой объект после push в реактивный массив UI бы не обновлял).
    const entries: PendingFile[] = files.map((file) =>
        reactive({
            file,
            status: "uploading" as const,
            progress: 0,
            sendSize: file.size,
            thumb: file.type.startsWith("image/")
                ? URL.createObjectURL(file)
                : undefined,
        })
    );
    pending.value.push(...entries);

    await Promise.all(entries.map(uploadEntry));
}

// Переключили оптимизацию — перезаливаем затронутые картинки: отменяем/чистим
// старую версию на сервере и запускаем загрузку заново с новой настройкой.
watch(optimize, () => {
    for (const entry of pending.value) {
        if (!canOptimize(entry.file)) {
            continue;
        }
        if (entry.status === "uploading") {
            entry.controller?.abort();
        } else if (entry.attachment) {
            void deleteFile(entry.attachment.url);
        }
        void uploadEntry(entry);
    }
});

function disposeEntry(entry: PendingFile): void {
    if (entry.status === "uploading") {
        // Транспорт сам удалит недокачанный файл на сервере.
        entry.controller?.abort();
    } else if (entry.attachment) {
        void deleteFile(entry.attachment.url);
    }
    if (entry.thumb) {
        URL.revokeObjectURL(entry.thumb);
    }
}

function remove(index: number) {
    const [entry] = pending.value.splice(index, 1);
    if (entry) {
        disposeEntry(entry);
    }
}

// Отмена (×, ESC, оверлей, кнопка): чистим всё, включая залитое на сервер.
function cancelAll() {
    for (const entry of pending.value) {
        disposeEntry(entry);
    }
    pending.value = [];
    caption.value = "";
}

function submit() {
    const attachments = pending.value
        .filter((p) => p.status === "done" && p.attachment)
        .map((p) => p.attachment!);
    if (!attachments.length || uploading.value) {
        return;
    }
    emit("send", caption.value.trim(), attachments);
    // Файлы отправлены — на сервере их оставляем, чистим только локальный стейт.
    for (const entry of pending.value) {
        if (entry.thumb) {
            URL.revokeObjectURL(entry.thumb);
        }
    }
    pending.value = [];
    caption.value = "";
}

// Триггер открытия выбора файла — кнопку рисует родитель (панель ChatEditor).
const inputRef = ref<HTMLInputElement | null>(null);
function open() {
    inputRef.value?.click();
}

defineExpose({ clear: cancelAll, open, addFiles });
</script>

<template>
    <AppModal
        :is-visible="modalVisible"
        title="Превью файлов"
        :max-width="420"
        @close="cancelAll"
    >
        <div class="mc-file-send">
            <div class="mc-file-send__list">
                <div
                    v-for="(p, i) in pending"
                    :key="i"
                    class="mc-file-send__row"
                    :class="{ 'mc-file-send__row_error': p.status === 'error' }"
                >
                    <!-- Полоса прогресса поверх строки при загрузке -->
                    <div
                        v-if="p.status === 'uploading'"
                        class="mc-file-send__bar"
                        :style="{
                            width:
                                Math.max(
                                    0,
                                    Math.min(100, Math.round(p.progress * 100))
                                ) + '%',
                        }"
                    />

                    <div class="mc-file-send__icon">
                        <img
                            v-if="p.thumb"
                            :src="p.thumb"
                            :alt="p.file.name"
                            class="mc-file-send__thumb"
                        />
                        <svg
                            v-else
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="1.6"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        >
                            <path d="M14 3v5h5" />
                            <path
                                d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"
                            />
                        </svg>
                    </div>

                    <div class="mc-file-send__meta">
                        <span class="mc-file-send__name" :title="p.file.name">
                            {{ p.file.name }}
                        </span>
                        <span class="mc-file-send__sub">
                            <template v-if="p.status === 'error'">
                                ОШИБКА: {{ p.error ?? "загрузка не удалась" }}
                            </template>
                            <template v-else>
                                {{ fileExt(p) }} · {{ formatSize(p.sendSize) }}
                                <span
                                    v-if="p.status === 'uploading'"
                                    class="mc-file-send__uploaded"
                                >
                                    ({{
                                        formatSize(
                                            Math.round(p.progress * p.sendSize)
                                        )
                                    }})
                                </span>
                            </template>
                        </span>
                    </div>

                    <button
                        class="mc-file-send__remove"
                        type="button"
                        :aria-label="
                            p.status === 'uploading' ? 'Отменить' : 'Удалить'
                        "
                        @click="remove(i)"
                    >
                        <svg
                            width="12"
                            height="12"
                            viewBox="0 0 12 12"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="1.6"
                            stroke-linecap="round"
                        >
                            <path d="M2 2l8 8M10 2l-8 8" />
                        </svg>
                    </button>
                </div>
                <!-- Добавление файлов — сразу под списком, компактной кнопкой. -->
                <AppButton
                    variant="ghost"
                    size="sm"
                    class="mc-file-send__add"
                    @click="open"
                >
                    Добавить файлы
                </AppButton>
            </div>

            <!-- Только когда есть что жать: видео/файлы не оптимизируем. -->
            <!-- <AppCheckbox v-if="hasOptimizable" v-model="optimize">
                Оптимизировать изображения
            </AppCheckbox> -->

            <input
                v-model="caption"
                class="mc-file-send__caption"
                type="text"
                placeholder="Подпись…"
                @keydown.enter.prevent="submit"
            />

            <div class="mc-file-send__actions">
                <AppButton variant="danger" @click="cancelAll">
                    Отмена
                </AppButton>
                <AppButton
                    variant="primary"
                    :disabled="uploading || !readyCount"
                    @click="submit"
                >
                    Отправить
                </AppButton>
            </div>
        </div>
    </AppModal>

    <input
        ref="inputRef"
        type="file"
        multiple
        style="display: none"
        :disabled="disabled"
        @change="onFileChange"
    />
</template>

<style scoped lang="scss">
.mc-file-send {
    display: flex;
    flex-direction: column;
    gap: 14px;

    &__list {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    &__row {
        position: relative;
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 10px 12px;
        background: var(--mc-bg-input);
        border: 1px solid var(--mc-line);
        overflow: hidden;

        &_error {
            border-color: var(--mc-danger);
        }
    }

    &__bar {
        position: absolute;
        top: 0;
        left: 0;
        height: 2px;
        background: var(--mc-acid);
        box-shadow: 0 0 6px var(--mc-acid-glow);
        transition: width 0.15s ease;
    }

    &__icon {
        flex: 0 0 auto;
        width: 44px;
        height: 44px;
        display: flex;
        align-items: center;
        justify-content: center;
        border: 1px solid var(--mc-line-hard);
        background: var(--mc-bg-rail);
        color: var(--mc-acid);
        overflow: hidden;
    }

    &__thumb {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }

    &__meta {
        display: flex;
        flex-direction: column;
        gap: 3px;
        min-width: 0;
    }

    &__name {
        font-size: 13px;
        font-weight: 700;
        color: var(--mc-fg);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    &__sub {
        font-family: var(--mc-mono);
        font-size: 10px;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: var(--mc-fg-mute);
        white-space: nowrap;
    }

    &__row_error &__sub {
        color: var(--mc-danger);
    }

    &__uploaded {
        color: var(--mc-acid);
    }

    &__remove {
        flex: 0 0 auto;
        // На уровне имени файла (первой строки), а не по центру строки.
        align-self: flex-start;
        margin-top: -2px;
        margin-left: auto;
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        background: none;
        border: none;
        color: var(--mc-fg-dim);
        cursor: pointer;
        transition:
            color 0.12s ease,
            background 0.12s ease;

        &:hover {
            color: var(--mc-fg);
            background: var(--mc-bg-sel);
        }
    }

    &__caption {
        width: 100%;
        padding: 9px 12px;
        background: var(--mc-bg-input);
        border: 1px solid var(--mc-line);
        color: var(--mc-fg);
        font-size: 14px;
        transition: border-color 0.15s;

        &::placeholder {
            color: var(--mc-fg-dim);
        }

        &:focus {
            outline: none;
            border-color: var(--mc-line-hard);
        }
    }

    // Компактная кнопка добавления под списком (перебиваем width:100% mc-btn).
    & &__add {
        width: auto;
        min-height: 0;
        align-self: flex-start;
        margin-left: auto;
        margin-bottom: 20px;
    }

    &__actions {
        display: flex;
        gap: 8px;
    }
}
</style>
